import type { ReactNode } from 'react';
import styles from './FormField.module.scss';

interface FormFieldProps {
  id: string;
  label?: string;
  isRequired?: boolean;
  helperText?: string;
  error?: string;
  isDisabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function FormField({
  id,
  label,
  isRequired,
  helperText,
  error,
  className,
  children,
}: FormFieldProps) {
  const hasError = Boolean(error);

  const fieldClasses = [
    styles.field,
    hasError && styles.error,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={fieldClasses}>
      {label && (
        // `id` lets a non-labelable control (e.g. MultiSelect's role="combobox"
        // div, which <label htmlFor> can't name) reference this via aria-labelledby.
        <label id={`${id}-label`} htmlFor={id} className={styles.label}>
          <span>{label}</span>
          {isRequired && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {children}

      {hasError ? (
        <p id={`${id}-error`} className={styles.errorText} role="alert">
          <span className={styles.errorIcon} aria-hidden="true">
            <svg
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0Zm.75 4a.75.75 0 0 0-1.5 0v3a.75.75 0 0 0 1.5 0V4ZM7 9.5a.875.875 0 1 0 0 1.75.875.875 0 0 0 0-1.75Z"
                fill="currentColor"
              />
            </svg>
          </span>
          {error}
        </p>
      ) : helperText ? (
        <p id={`${id}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
